/**
 * Retry utilities with exponential backoff
 *
 * Provides robust retry functionality for handling transient failures in network operations.
 */

/**
 * Executes an operation with exponential backoff retry logic
 *
 * @template T - The return type of the operation
 * @param operation - The async operation to retry
 * @param operationName - Human-readable name for logging purposes
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param baseDelay - Base delay in milliseconds between retries (default: 1000)
 * @returns Promise that resolves to the operation result
 * @throws {Error} If the operation fails after all retry attempts
 */
export async function retryWithExponentialBackoff<T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: unknown = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      console.warn(
        `Error ${operationName}, retrying... attempt: ${attempt} / ${maxRetries}, error: ${error}`
      );

      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        // Normalize the underlying value: a non-Error rejection (e.g. a bare
        // string) has no `.message`, which previously produced "...: undefined".
        const errorMessage =
          lastError instanceof Error
            ? lastError.message
            : JSON.stringify(lastError);
        // Preserve the original error via `cause` so debugging context survives.
        const wrappedError = new Error(
          `Failed ${operationName} after ${maxRetries + 1} attempts: ${errorMessage}`
        );
        (wrappedError as Error & { cause?: unknown }).cause = lastError;
        throw wrappedError;
      }

      // Calculate exponential backoff delay
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // This should never be reached, but satisfies TypeScript
  throw lastError instanceof Error
    ? lastError
    : new Error(`Failed ${operationName}: ${JSON.stringify(lastError)}`);
}
